// src/components/Experiment/ResultsSummaryCard.js
import React, { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Card, Table, Badge, Alert, Spinner, OverlayTrigger, Tooltip, Button, Modal, Form } from 'react-bootstrap';
import { deleteAnalysis, getAnalysisSummaries, updateAnalysisSummaryDescription, batchDeleteAnalyses } from '../../api/photoProcessingApi';
import './ResultsSummaryCard.css';

const ResultsSummaryCard = forwardRef(({ experimentId, analyses, onDeleteCombination, onAnalysesUpdate }, ref) => {
  const [expanded, setExpanded] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCombination, setDeletingCombination] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [summaries, setSummaries] = useState(null);
  const [loadingSummaries, setLoadingSummaries] = useState(false);
  const [editingDescription, setEditingDescription] = useState(null);
  const [editDescriptionText, setEditDescriptionText] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => {
      setRefreshTrigger(prev => prev + 1);
    },
    forceRefresh: () => {
      setRefreshTrigger(prev => prev + 1);
    }
  }));

  // Load summaries from API - depends on refreshTrigger
  useEffect(() => {
    if (experimentId) {
      loadSummaries();
    }
  }, [experimentId, refreshTrigger]);

  const loadSummaries = async (retryCount = 0) => {
    try {
      setLoadingSummaries(true);
      const response = await getAnalysisSummaries(experimentId);
      if (response.success && response.results) {
        // Filter out summaries with no entries (empty rows)
        const nonEmptySummaries = response.results.filter(summary =>
          summary.entries && summary.entries.length > 0
        );
        setSummaries(nonEmptySummaries);

        // If we have summaries but they're still updating, retry after a delay
        const hasPendingUpdates = nonEmptySummaries.some(summary =>
          summary.entries && summary.entries.length === 0
        );

        if (hasPendingUpdates && retryCount < 3) {
          console.log(`Summaries appear incomplete, retrying (${retryCount + 1}/3)...`);
          setTimeout(() => loadSummaries(retryCount + 1), 1000);
        }
      }
    } catch (error) {
      console.error('Error loading summaries:', error);
      if (retryCount < 3) {
        console.log(`Retrying summary load (${retryCount + 1}/3)...`);
        setTimeout(() => loadSummaries(retryCount + 1), 1000);
      }
    } finally {
      setLoadingSummaries(false);
    }
  };

  // Handle click on an accuracy entry: scroll to the analysis card
  const handleEntryClick = (e, analysisUuid) => {
    e.preventDefault();
    const element = document.getElementById(`analysis-${analysisUuid}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Optionally highlight the card (add a class) - not implemented here
    }
  };

  // Process analyses data to create label+range combinations vs analysis type matrix
  const { labelRangeCombinations, analysisTypes, summaryMatrix, allData, combinationAnalysesMap } = useMemo(() => {
    // If we have summaries from API, use them preferentially
    if (summaries && summaries.length > 0) {
      const combinationSet = new Set();
      const analysisTypesSet = new Set();
      const dataMap = new Map();
      const entriesMap = new Map(); // key: combinationId|analysisType -> array of entries
      const allDataItems = [];
      const combinationMap = new Map();

      summaries.forEach(summary => {
        const combinationId = summary.combination_id;
        const summaryId = summary.id; // Get the actual UUID
        const labelColumn = summary.label_column;
        const dataRange = summary.data_range;
        const includedClasses = summary.included_classes || [];
        const totalClasses = summary.total_classes || [];

        combinationSet.add(combinationId);
        dataMap.set(combinationId, {
          id: summaryId, // Store the UUID
          labelColumn,
          dataRange,
          includedClasses,
          totalClasses,
          isEnumerated: summary.is_enumerated,
          combinationId,
          description: summary.description
        });

        combinationMap.set(combinationId, summary.analysis_ids || []);

        // Process entries
        summary.entries?.forEach(entry => {
          const analysisType = entry.analysis_type;
          analysisTypesSet.add(analysisType);

          const key = `${combinationId}|${analysisType}`;
          if (!entriesMap.has(key)) {
            entriesMap.set(key, []);
          }

          // Build entry object with all needed fields
          const entryObj = {
            id: entry.id,
            analysisType: entry.analysis_type,
            accuracy: entry.accuracy,
            accuracyStd: entry.accuracy_std,
            permutationCvPValue: entry.permutation_cv_p_value,
            analysisUuid: entry.analysis_id,
            status: 'completed', // all entries from summaries are completed
            name: entry.additional_metrics?.analysis_name || '',
            createdAt: entry.created_at,
            summaryId: summaryId, // reference to summary
            combinationId: combinationId,
          };
          entriesMap.get(key).push(entryObj);

          allDataItems.push({
            id: summaryId,
            labelColumn,
            dataRange,
            includedClasses,
            totalClasses,
            isEnumerated: summary.is_enumerated,
            combinationId,
            analysisType,
            cvAccuracy: entry.accuracy,
            cvStd: entry.accuracy_std,
            analysisId: entry.analysis_id,
            status: 'completed',
            name: entry.additional_metrics?.analysis_name || '',
            createdAt: entry.created_at,
          });
        });
      });

      // Sort entries within each key by created_at (oldest first)
      entriesMap.forEach((entries, key) => {
        entries.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });

      const labelRangeCombinations = Array.from(combinationSet)
        .map(id => dataMap.get(id))
        .sort((a, b) => {
          if (a.labelColumn !== b.labelColumn) {
            return a.labelColumn.localeCompare(b.labelColumn);
          }
          return (b.includedClasses?.length || 0) - (a.includedClasses?.length || 0);
        });

      const analysisTypes = Array.from(analysisTypesSet).sort();

      // Convert entriesMap to summaryMatrix (object mapping key -> array of entries)
      const summaryMatrix = {};
      entriesMap.forEach((value, key) => {
        summaryMatrix[key] = value;
      });

      return {
        labelRangeCombinations,
        analysisTypes,
        summaryMatrix,
        allData: allDataItems,
        combinationAnalysesMap: combinationMap
      };
    }

    // Fallback to processing analyses directly (existing code)
    if (!analyses || analyses.length === 0) {
      return {
        labelRangeCombinations: [],
        analysisTypes: [],
        summaryMatrix: {},
        allData: [],
        combinationAnalysesMap: new Map()
      };
    }

    // Fallback processing - group all analyses by combination and analysis type
    const combinationSet = new Set();
    const analysisTypesSet = new Set();
    const dataMap = new Map();
    const entriesMap = new Map(); // key: combinationId|analysisType -> array of entries
    const allDataItems = [];
    const combinationMap = new Map();

    analyses.forEach(analysis => {
      const labelColumn = analysis.label_column || 'Unknown';
      const dataRange = analysis.data_range || 'Unknown';
      const analysisType = ((analysis.analysis_type?.toUpperCase() || 'UNKNOWN') !== 'PIPELINE'
        ? analysis.analysis_type?.toUpperCase()
        : analysis.analysis_type?.toUpperCase() + ` (${analysis.results?.classifier_used || ''})`);

      const results = analysis.results;
      const parameters = analysis.parameters || {};
      const isEnumerated = parameters.is_enumerated || false;
      const includedClasses = parameters.included_classes || [];
      const totalClasses = parameters.total_classes || [];

      if (!results) return;

      const isClassification = [
        'lda', 'logistic', 'svm', 'random_forest', 'plsda', 'knn', 'simca', 'pipeline', 'xgboost', 'bbc', 'brf'
      ].includes(analysis.analysis_type);

      if (!isClassification) return;

      let cvAccuracy = null;
      let cvStd = null;

      if (results.cv_mean !== undefined) {
        cvAccuracy = results.cv_mean;
        cvStd = results.cv_std || 0;
      } else if (results.cv_accuracy !== undefined) {
        cvAccuracy = results.cv_accuracy;
        cvStd = results.cv_accuracy_std || 0;
      } else if (results.accuracy !== undefined) {
        cvAccuracy = results.accuracy;
      }

      if (cvAccuracy !== null) {
        const includedClassesKey = includedClasses.length > 0 ? includedClasses.join('+') : 'all';
        const combinationId = `${labelColumn}|${dataRange}|${includedClassesKey}`;

        if (!dataMap.has(combinationId)) {
          dataMap.set(combinationId, {
            id: combinationId, // Use combinationId as fallback
            labelColumn,
            dataRange,
            includedClasses,
            totalClasses,
            isEnumerated,
            combinationId
          });
          combinationSet.add(combinationId);
        }

        if (!combinationMap.has(combinationId)) {
          combinationMap.set(combinationId, []);
        }
        combinationMap.get(combinationId).push(analysis.id);

        analysisTypesSet.add(analysisType);

        const key = `${combinationId}|${analysisType}`;
        if (!entriesMap.has(key)) {
          entriesMap.set(key, []);
        }

        // Build entry object
        const entryObj = {
          analysisType: analysisType,
          accuracy: cvAccuracy,
          accuracyStd: cvStd,
          permutationCvPValue: (analysis.visualizations || [])
            .map(v => v.permutation_stats?.cv_p_value)
            .find(p => typeof p === 'number') ?? null,
          analysisUuid: analysis.id,
          status: analysis.status,
          name: analysis.name || '',
          createdAt: analysis.created_at || analysis.updated_at,
          combinationId: combinationId,
        };
        entriesMap.get(key).push(entryObj);

        allDataItems.push({
          id: combinationId,
          labelColumn,
          dataRange,
          includedClasses,
          totalClasses,
          isEnumerated,
          combinationId,
          analysisType,
          cvAccuracy,
          cvStd,
          analysisId: analysis.id,
          status: analysis.status,
          name: analysis.name,
          createdAt: analysis.created_at || analysis.updated_at,
        });
      }
    });

    // Sort entries within each key by created_at (oldest first)
    entriesMap.forEach((entries, key) => {
      entries.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    });

    const labelRangeCombinations = Array.from(combinationSet)
      .map(id => dataMap.get(id))
      .sort((a, b) => {
        if (a.labelColumn !== b.labelColumn) {
          return a.labelColumn.localeCompare(b.labelColumn);
        }
        return (b.includedClasses?.length || 0) - (a.includedClasses?.length || 0);
      });

    const analysisTypes = Array.from(analysisTypesSet).sort();

    const summaryMatrix = {};
    entriesMap.forEach((value, key) => {
      summaryMatrix[key] = value;
    });

    allDataItems.sort((a, b) => b.cvAccuracy - a.cvAccuracy);

    return {
      labelRangeCombinations,
      analysisTypes,
      summaryMatrix,
      allData: allDataItems,
      combinationAnalysesMap: combinationMap
    };
  }, [analyses, summaries]);

  const handleEditDescription = (combination) => {
    // Use the summary's id (UUID) instead of combinationId
    setEditingDescription(combination.id);
    setEditDescriptionText(combination.description || '');
  };

  const handleSaveDescription = async (combination) => {
    try {
      // Use the summary's id (UUID) for the API call
      await updateAnalysisSummaryDescription(experimentId, combination.id, editDescriptionText);
      // Reload summaries to get updated data
      await loadSummaries();
      setEditingDescription(null);
      setEditDescriptionText('');
    } catch (error) {
      console.error('Error saving description:', error);
      alert('Failed to save description: ' + error.message);
    }
  };

  const formatClassesDisplay = (includedClasses, totalClasses, isEnumerated) => {
    if (!includedClasses || includedClasses.length === 0) {
      return <span className="text-muted fst-italic">All classes</span>;
    }

    if (includedClasses.length === totalClasses?.length) {
      return (
        <>
          <span className="text-muted fst-italic">All classes: </span>
          {includedClasses.join(', ')}
        </>
      );
    }

    return includedClasses.join(', ');
  };

  const getClassesTooltip = (includedClasses, totalClasses) => {
    if (!includedClasses || includedClasses.length === 0) return '';

    const excludedClasses = totalClasses?.filter(c => !includedClasses.includes(c)) || [];

    let tooltip = `Included: ${includedClasses.join(', ')}`;
    if (excludedClasses.length > 0) {
      tooltip += `\nExcluded: ${excludedClasses.join(', ')}`;
    }

    return tooltip;
  };

  const formatMetricValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'number') {
      if (value > 1) {
        return value.toFixed(1) + '%';
      } else {
        return (value * 100).toFixed(1) + '%';
      }
    }
    return value;
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy === null || accuracy === undefined) return 'secondary';

    const numericAccuracy = typeof accuracy === 'number'
      ? (accuracy > 1 ? accuracy / 100 : accuracy)
      : 0;
    if (numericAccuracy > 0.99) return 'danger';
    if (numericAccuracy >= 0.9) return 'success';
    if (numericAccuracy >= 0.8) return 'primary';
    return 'warning';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="status-badge completed">✓</span>;
      case 'running':
        return <span className="status-badge running">
          <Spinner animation="border" size="sm" className="me-1" />
        </span>;
      case 'failed':
        return <span className="status-badge failed">✗</span>;
      default:
        return <span className="status-badge pending">?</span>;
    }
  };

  const handleDeleteClick = (combination) => {
    setDeletingCombination(combination);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCombination) return;

    try {
      setIsDeleting(true);
      const analysisIds = combinationAnalysesMap.get(deletingCombination.combinationId) || [];

      // Use batch delete API for better performance
      const response = await batchDeleteAnalyses(analysisIds);

      if (response.success) {
        console.log(`Deleted ${response.deleted_count} analyses from combination`);

        if (onDeleteCombination) {
          onDeleteCombination(analysisIds);
        }

        if (onAnalysesUpdate) {
          onAnalysesUpdate();
        }

        // Force refresh summaries after deletion
        setRefreshTrigger(prev => prev + 1);

        setShowDeleteModal(false);
        setDeletingCombination(null);
      } else {
        throw new Error(response.error || 'Batch delete failed');
      }
    } catch (error) {
      console.error('Error deleting combination analyses:', error);
      alert('Failed to delete analyses: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingCombination(null);
  };

  if (loadingSummaries) {
    return (
      <Card className="mb-4 summary-card">
        <Card.Header>
          <h5 className="mb-0">Analysis Performance Summary</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">Loading summary data...</p>
        </Card.Body>
      </Card>
    );
  }

  if (labelRangeCombinations.length === 0 || analysisTypes.length === 0) {
    return (
      <Card className="mb-4 summary-card">
        <Card.Header>
          <h5 className="mb-0">Analysis Performance Summary</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Alert variant="info" className="mb-0">
            <i className="bi bi-bar-chart me-2"></i>
            No classification analyses found. Run LDA, PLS-DA, SVM, or other classification methods to see performance summary.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-4 summary-card">
        <Card.Header
          className="d-flex justify-content-between align-items-center summary-card-header"
          onClick={() => setExpanded(!expanded)}
          style={{ cursor: 'pointer' }}
        >
          <div>
            <h5 className="mb-0">
              <i className={`bi bi-chevron-${expanded ? 'down' : 'right'} me-2`}></i>
              Analysis Performance Summary
            </h5>
            <small className="text-muted">
              {labelRangeCombinations.length} label configuration{labelRangeCombinations.length !== 1 ? 's' : ''}, {analysisTypes.length} method{analysisTypes.length !== 1 ? 's' : ''}
            </small>
          </div>
          <Badge bg="light" text="dark" className="summary-badge">
            {allData.filter(d => d.status === 'completed').length} completed
          </Badge>
        </Card.Header>

        {expanded && (
          <>
            <Card.Body className="p-0">
              <div className="table-container">
                <Table hover className="summary-table mb-0">
                  <thead>
                    <tr>
                      <th className="label-column-header">Label Column</th>
                      <th className="classes-column-header">Included Classes</th>
                      <th className="data-range-header">Data Range</th>
                      <th className="description-header">Description</th>
                      {analysisTypes.map(type => (
                        <th key={type} className="text-center analysis-type-header">
                          {type}
                        </th>
                      ))}
                      <th className="actions-header text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {labelRangeCombinations.map((combination, index) => {
                      const combinationId = combination.combinationId;
                      const analysisCount = combinationAnalysesMap.get(combinationId)?.length || 0;
                      const isEditing = editingDescription === combination.id;

                      return (
                        <tr key={combinationId} className="label-row">
                          <td className="label-cell">
                            <strong>{combination.labelColumn}</strong>
                          </td>
                          <td className="classes-cell">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-${index}`}>
                                  {getClassesTooltip(combination.includedClasses, combination.totalClasses)}
                                </Tooltip>
                              }
                            >
                              <div className="classes-list" style={{ cursor: 'help' }}>
                                {formatClassesDisplay(
                                  combination.includedClasses,
                                  combination.totalClasses,
                                  combination.isEnumerated
                                )}
                              </div>
                            </OverlayTrigger>
                          </td>
                          <td className="data-range-cell">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`range-tooltip-${index}`}>
                                  {combination.dataRange}
                                </Tooltip>
                              }
                            >
                              <span className="data-range-text" style={{ cursor: 'help' }}>
                                {combination.dataRange.length > 40
                                  ? `${combination.dataRange.substring(0, 40)}...`
                                  : combination.dataRange}
                              </span>
                            </OverlayTrigger>
                          </td>
                          <td className="description-cell">
                            {isEditing ? (
                              <div className="description-edit-container">
                                <Form.Control
                                  type="text"
                                  className="description-input"
                                  value={editDescriptionText}
                                  onChange={(e) => setEditDescriptionText(e.target.value)}
                                  placeholder="Add a meaningful description for this configuration..."
                                  autoFocus
                                />
                                <div className="description-edit-buttons">
                                  <Button size="sm" variant="success" onClick={() => handleSaveDescription(combination)}>
                                    <i className="bi bi-check"></i> Save
                                  </Button>
                                  <Button size="sm" variant="secondary" onClick={() => setEditingDescription(null)}>
                                    <i className="bi bi-x"></i> Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="description-display">
                                <span className="description-text">
                                  {combination.description ? (
                                    combination.description
                                  ) : (
                                    <span className="text-muted fst-italic">Click edit to add description...</span>
                                  )}
                                </span>
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="edit-description-btn"
                                  onClick={() => handleEditDescription(combination)}
                                  title="Edit description"
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </Button>
                              </div>
                            )}
                          </td>
                          {analysisTypes.map(type => {
                            const key = `${combinationId}|${type}`;
                            const entries = summaryMatrix[key] || [];

                            if (entries.length === 0) {
                              return (
                                <td key={type} className="text-center empty-cell">
                                  <span className="text-muted">—</span>
                                </td>
                              );
                            }

                            return (
                              <td key={type} className="text-center data-cell">
                                <div className="accuracy-container">
                                  {entries.map((entry, idx) => {
                                    const accuracyValue = entry.accuracy;
                                    const permutationP = entry.permutationCvPValue;
                                    const notSignificant = typeof permutationP === 'number' && permutationP > 0.05;
                                    const status = entry.status || 'completed';
                                    const analysisUuid = entry.analysisUuid;
                                    return (
                                      <div key={idx} className="accuracy-entry d-flex align-items-center justify-content-center mb-1">
                                        <a
                                          href={`#analysis-${analysisUuid}`}
                                          className="text-decoration-none d-flex align-items-center"
                                          style={{ cursor: 'pointer' }}
                                          onClick={(e) => handleEntryClick(e, analysisUuid)}
                                        >
                                          {getStatusBadge(status)}
                                          <Badge
                                            bg={notSignificant ? 'secondary' : getAccuracyColor(accuracyValue)}
                                            className="accuracy-badge ms-1"
                                            title={notSignificant
                                              ? `Permutation test: CV p-value = ${permutationP.toFixed(3)} (> 0.05), accuracy is not significant`
                                              : undefined}
                                          >
                                            {formatMetricValue(accuracyValue)}
                                          </Badge>
                                        </a>
                                      </div>
                                    );
                                  })}
                                </div>
                              </td>
                            );
                          })}
                          <td className="actions-cell text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteClick(combination)}
                              className="delete-combination-btn"
                              title={`Delete all ${analysisCount} analysis/analyses for this configuration`}
                            >
                              <i className="bi bi-trash"></i>
                              <Badge bg="danger" className="ms-1 analysis-count-badge">
                                {analysisCount}
                              </Badge>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Delete Configuration Analyses
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deletingCombination && (
            <>
              <Alert variant="danger">
                <i className="bi bi-exclamation-octagon-fill me-2"></i>
                <strong>Warning: This action cannot be undone!</strong>
              </Alert>

              <p>
                You are about to delete all analyses for the following configuration:
              </p>

              <div className="config-details mb-3 p-3 bg-light rounded">
                <p className="mb-2">
                  <strong>Label Column:</strong> {deletingCombination.labelColumn}
                </p>
                <p className="mb-2">
                  <strong>Data Range:</strong> {deletingCombination.dataRange}
                </p>
                <p className="mb-0">
                  <strong>Included Classes:</strong>{' '}
                  {deletingCombination.includedClasses.length === 0 ||
                   deletingCombination.includedClasses.length === deletingCombination.totalClasses?.length
                    ? 'All classes'
                    : deletingCombination.includedClasses.join(', ')}
                </p>
                {deletingCombination.description && (
                  <p className="mb-0 mt-2">
                    <strong>Description:</strong> {deletingCombination.description}
                  </p>
                )}
              </div>

              <p>
                This will permanently remove{' '}
                <strong>{combinationAnalysesMap.get(deletingCombination.combinationId)?.length || 0}</strong>{' '}
                analysis/analyses and all associated visualizations.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="bi bi-trash-fill me-2"></i>
                Delete All Analyses
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

ResultsSummaryCard.displayName = 'ResultsSummaryCard';

export default ResultsSummaryCard;